import { isEqual } from 'lodash-es';

/**
 * 获取商品稳定标识，用于匹配新旧数据中的同一商品
 */
function getProductIdentity(item: Record<string, any>, index: number): string {
  return item.shop_sku || String(item.id ?? '') || `idx-${index}`;
}

/**
 * 对比两个商品数组，返回合并结果
 * - 相同商品保留旧引用（维持 React memo 稳定性）
 * - 仅变化的商品使用新引用
 */
function diffProductArrays(
  oldProducts: Record<string, any>[],
  newProducts: Record<string, any>[],
): { changed: boolean; mergedArray: Record<string, any>[] } {
  const oldMap = new Map<string, { item: Record<string, any>; index: number }>();
  oldProducts.forEach((item, index) => {
    oldMap.set(getProductIdentity(item, index), { item, index });
  });

  let hasChange = false;
  const merged: Record<string, any>[] = [];

  for (let i = 0; i < newProducts.length; i++) {
    const newItem = newProducts[i];
    const newKey = getProductIdentity(newItem, i);
    const oldEntry = oldMap.get(newKey);

    if (oldEntry) {
      if (isEqual(oldEntry.item, newItem)) {
        merged.push(oldEntry.item);
        // 内容相同但位置变化（个性化排序、推荐重排等）
        if (oldEntry.index !== i) {
          hasChange = true;
        }
      } else {
        merged.push(newItem);
        hasChange = true;
      }
      oldMap.delete(newKey);
    } else {
      merged.push(newItem);
      hasChange = true;
    }
  }

  if (oldMap.size > 0) {
    hasChange = true;
  }

  if (!hasChange) {
    return { changed: false, mergedArray: oldProducts };
  }

  return { changed: true, mergedArray: merged };
}

function omitKeys<T extends Record<string, any>>(data: T, keys: string[]): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key of Object.keys(data)) {
    if (!keys.includes(key)) {
      result[key] = data[key];
    }
  }
  return result;
}

export interface DiffResult<T> {
  changed: boolean;
  mergedData: T | null;
}

/**
 * 对比新旧商品数据，仅在存在差异时返回合并结果
 * - 新数据为 null 时保留旧数据（防骨架屏闪现）
 * - 相同商品保留旧引用（维持 React memo 稳定性）
 */
export function diffProductData<T extends Record<string, any>>(
  oldData: T | null,
  newData: T | null,
  productKeys: string[] = ['products', 'products_2'],
): DiffResult<T> {
  if (oldData === null && newData === null) {
    return { changed: false, mergedData: oldData };
  }

  if (oldData === null && newData !== null) {
    return { changed: true, mergedData: newData };
  }

  // 旧有数据、新为 null → 保留旧数据
  if (oldData !== null && newData === null) {
    return { changed: false, mergedData: oldData };
  }

  // 均有数据 → 以 newData 为基底，未变化的商品数组替换回旧引用
  let hasAnyChange = false;
  const mergedResult: Record<string, any> = { ...newData };

  for (const key of productKeys) {
    const oldArr = (oldData as Record<string, any>)[key];
    const newArr = (newData as Record<string, any>)[key];

    if (!oldArr && !newArr) continue;

    if (!oldArr && newArr) {
      // 新增的字段，保持 newData 的值
      hasAnyChange = true;
      continue;
    }

    if (oldArr && !newArr) {
      // 字段级 null/undefined 是后端业务语义，显式写入以覆盖 indexData 的 fallback
      mergedResult[key] = newArr;
      hasAnyChange = true;
      continue;
    }

    const diff = diffProductArrays(oldArr, newArr);
    if (diff.changed) {
      mergedResult[key] = diff.mergedArray;
      hasAnyChange = true;
    } else {
      // 内容和顺序都相同，换回旧引用保持 memo 稳定性
      mergedResult[key] = oldArr;
    }
  }

  // 非商品字段已在 { ...newData } 中，对比是否有变化
  const oldMeta = omitKeys(oldData!, productKeys);
  const newMeta = omitKeys(newData!, productKeys);
  if (!isEqual(oldMeta, newMeta)) {
    hasAnyChange = true;
  }

  return { changed: hasAnyChange, mergedData: hasAnyChange ? (mergedResult as T) : oldData };
}
